import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { 
  Home,
  FileText,
  Shield,
  Zap,
  DollarSign,
  Calendar,
  Menu,
  X,
  Network,
  Users,
  ListTodo,
  ChevronDown,
  ChevronRight,
  LogOut
} from 'lucide-react';
import AuthGuard from './components/AuthGuard';
import './App.css';

// Import the demo components
import ConstructionRAGDemo from './components/ConstructionRAGDemo';
import ConstructionAgentsDemo from './components/ConstructionAgentsDemo';
import ConstructionMoEDemo from './components/ConstructionMoEDemo';
import PolicyContentIntentDemo from './components/PolicyContentIntentDemo';
import Sprint10Report from './reports/Sprint10Report';
import Sep4StatusReport from './reports/Sep4StatusReport';
import Sep29Report from './reports/Sep29Report';
import Oct27Report from './reports/Oct27Report';
import Nov24Report from './reports/Nov24Report';
import NovStatusReport from './reports/NovStatusReport';
import Jan19StatusReport from './reports/Jan19StatusReport';
import Feb2ExecutiveSummary from './reports/Feb2ExecutiveSummary';
import Feb9Report from './reports/Feb9Report';
import Feb24Report from './reports/Feb24Report';
import InsuranceComparisonWireframe from './components/InsuranceComparisonWireframe';
import SingleDocumentCheck from './components/SingleDocumentCheck';
import RiskGuruMindmap from './components/RiskGuruMindmap';
import TaskRouter from './components/TaskRouter';
import CustomerEngagementReport from './reports/CustomerEngagementReport';
import CustomerEngagementReportInternal from './reports/CustomerEngagementReportInternal';
import CustomerEngagementReportSummary from './reports/CustomerEngagementReportSummary';
import DecJanCostReport from './reports/DecJanCostReport';
import LLMClassificationDemo from './components/LLMClassificationDemo';
import AICostCalculator from './components/AICostCalculator';

const Navigation = ({ isCollapsed, toggleCollapse }) => {
  const location = useLocation();
  const { instance } = useMsal();
  const [expandedSections, setExpandedSections] = useState({
    statusReports: true,
    tools: true,
    customerEngagement: true,
    reports: true
  });

  const toggleSection = (section) => {
    if (isCollapsed) return;
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const navStructure = [
    {
      type: 'item',
      path: '/',
      name: 'Home',
      icon: <Home className="w-5 h-5" />,
      description: 'Welcome to AI Presentations'
    },
    {
      type: 'section',
      key: 'statusReports',
      name: 'Status Reports',
      icon: <Calendar className="w-5 h-5" />,
      children: [
        {
          path: '/sep4-status-report',
          name: 'Sep 4th 2025',
          icon: <Calendar className="w-4 h-4" />,
          description: 'AI Technology Demonstrations'
        },
        {
          path: '/status-report',
          name: 'Sep 15th 2025',
          icon: <FileText className="w-4 h-4" />,
          description: 'Sprint 10 & Baldwin Pilot Progress'
        },
        {
          path: '/sep29-report',
          name: 'Sep 29th 2025',
          icon: <Calendar className="w-4 h-4" />,
          description: 'Sprint 11 Completion Report'
        },
        {
          path: '/oct27-report',
          name: 'Oct 27th 2025',
          icon: <Calendar className="w-4 h-4" />,
          description: 'Executive Update Report'
        },
        {
          path: '/nov24-report',
          name: 'Nov 24th 2025',
          icon: <Calendar className="w-4 h-4" />,
          description: 'Executive Update Report'
        },
        {
          path: '/nov-status-report',
          name: 'December Report',
          icon: <Calendar className="w-4 h-4" />,
          description: 'Last Two Weeks Status Update'
        },
        {
          path: '/jan19-status-report',
          name: 'January 19th 2025',
          icon: <Calendar className="w-4 h-4" />,
          description: 'Team & Product Updates'
        },
        {
          path: '/feb2-executive-summary',
          name: 'February 2nd 2026',
          icon: <Calendar className="w-4 h-4" />,
          description: 'Executive Summary - Last Two Weeks'
        },
        {
          path: '/feb9-report',
          name: 'February 9th 2025',
          icon: <Calendar className="w-4 h-4" />,
          description: 'Wrap & Asuretify Updates - Last Two Weeks'
        },
        {
          path: '/feb24-report',
          name: 'February 24th 2026',
          icon: <Calendar className="w-4 h-4" />,
          description: 'Weekly Report - Word/PDF, DOCX, Kinetic, Agent Portal'
        }
      ]
    },
    {
      type: 'section',
      key: 'tools',
      name: 'Tools & Demos',
      icon: <Zap className="w-5 h-5" />,
      children: [
        {
          path: '/insurance-comparison',
          name: 'Insurance Comparison',
          icon: <Shield className="w-4 h-4" />,
          description: 'Policy Comparison Tool'
        },
        {
          path: '/single-document-check',
          name: 'Single Document Check',
          icon: <FileText className="w-4 h-4" />,
          description: 'Individual Policy Analysis'
        },
        {
          path: '/riskguru-mindmap',
          name: 'RiskGuru Mind Map',
          icon: <Network className="w-4 h-4" />,
          description: 'Interactive Feature Mind Map'
        },
        {
          path: '/llm-classification',
          name: 'LLM Classification',
          icon: <Zap className="w-4 h-4" />,
          description: 'LLM Classification & Extraction Process'
        },
        {
          path: '/task-router',
          name: 'Task Router',
          icon: <ListTodo className="w-4 h-4" />,
          description: 'Lockton Task Routing Prototype'
        },
        {
          path: '/ai-cost-calculator',
          name: 'AI Cost Calculator',
          icon: <DollarSign className="w-4 h-4" />,
          description: 'Rough OpenAI/Whisperer/Textract cost estimates'
        }
      ]
    },
    {
      type: 'section',
      key: 'customerEngagement',
      name: 'Customer Engagement',
      icon: <Users className="w-5 h-5" />,
      children: [
        {
          path: '/customer-engagement',
          name: 'Summary',
          icon: <Users className="w-4 h-4" />,
          description: 'Customer Engagement Summary'
        },
        {
          path: '/customer-engagement-internal',
          name: 'Internal',
          icon: <Users className="w-4 h-4" />,
          description: 'Detailed Internal Report'
        },
        {
          path: '/customer-engagement-summary',
          name: 'Executive Summary',
          icon: <Users className="w-4 h-4" />,
          description: 'Executive Highlights'
        }
      ]
    },
    {
      type: 'section',
      key: 'reports',
      name: 'Reports',
      icon: <FileText className="w-5 h-5" />,
      children: [
        {
          path: '/cost-report',
          name: 'Cost Report',
          icon: <FileText className="w-4 h-4" />,
          description: 'Organization Cost Breakdown'
        }
      ]
    }
  ];

  const isPathActive = (path) => location.pathname === path;
  const isSectionActive = (children) => children.some(child => isPathActive(child.path));

  return (
    <nav className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <div className="logo">
          <Shield className="w-8 h-8 text-blue-600" />
          {!isCollapsed && <span className="logo-text">AI Presentations</span>}
        </div>
        <button 
          className="collapse-toggle"
          onClick={toggleCollapse}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
        </button>
      </div>
      
      <div className="nav-items">
        {navStructure.map((item) => {
          if (item.type === 'item') {
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isPathActive(item.path) ? 'active' : ''}`}
                title={isCollapsed ? item.name : ''}
              >
                <div className="nav-icon">{item.icon}</div>
                {!isCollapsed && (
                  <div className="nav-content">
                    <div className="nav-name">{item.name}</div>
                    <div className="nav-description">{item.description}</div>
                  </div>
                )}
              </Link>
            );
          } else {
            const isExpanded = expandedSections[item.key] && !isCollapsed;
            const hasActiveChild = isSectionActive(item.children);
            
            return (
              <div key={item.key} className="nav-section">
                <div
                  className={`nav-section-header ${hasActiveChild ? 'active' : ''}`}
                  onClick={() => toggleSection(item.key)}
                  title={isCollapsed ? item.name : ''}
                >
                  <div className="nav-icon">{item.icon}</div>
                  {!isCollapsed && (
                    <>
                      <div className="nav-content">
                        <div className="nav-name">{item.name}</div>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 nav-chevron" />
                      ) : (
                        <ChevronRight className="w-4 h-4 nav-chevron" />
                      )}
                    </>
                  )}
                </div>
                {isExpanded && (
                  <div className="nav-section-children">
                    {item.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        className={`nav-item nav-child ${isPathActive(child.path) ? 'active' : ''}`}
                        title={isCollapsed ? child.name : ''}
                      >
                        <div className="nav-icon">{child.icon}</div>
                        <div className="nav-content">
                          <div className="nav-name">{child.name}</div>
                          <div className="nav-description">{child.description}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          }
        })}
      </div>
      
      <div className="sidebar-footer">
        <button
          type="button"
          className="sign-out-btn"
          onClick={() => instance.logoutRedirect()}
          title="Sign out"
        >
          <LogOut className="w-4 h-4" />
          {!isCollapsed && <span>Sign out</span>}
        </button>
        <div className="footer-text">
          <Zap className="w-4 h-4 text-gray-400" />
          {!isCollapsed && <span>Construction Insurance AI</span>}
        </div>
      </div>
    </nav>
  );
};

const HomePage = () => {
  return (
    <div className="home-page">
      <div className="home-content">
        <h1 className="home-title">
          <Shield className="w-12 h-12 text-blue-600 mb-4" />
          AI Presentations
        </h1>
        <p className="home-subtitle">
        Transforming construction insurance through intelligent automation, 
        specialized expertise, and contextual understanding
        </p>
        
        <div className="demo-grid">
          <div className="demo-card">
            <Calendar className="w-8 h-8 text-blue-600 mb-3" />
            <h3>Sep 4th 2025</h3>
            <p>AI Technology Demonstrations</p>
            <Link to="/sep4-status-report" className="demo-link">View Demos →</Link>
          </div>
          
          <div className="demo-card">
            <FileText className="w-8 h-8 text-red-600 mb-3" />
            <h3>Sep 15th 2025</h3>
            <p>Review Sprint 10 progress and Baldwin pilot updates</p>
            <Link to="/status-report" className="demo-link">View Report →</Link>
          </div>
          
          <div className="demo-card">
            <Calendar className="w-8 h-8 text-green-600 mb-3" />
            <h3>Sep 29th 2025</h3>
            <p>Sprint 11 completion and Q3 OKR progress</p>
            <Link to="/sep29-report" className="demo-link">View Report →</Link>
          </div>
          
          <div className="demo-card">
            <Calendar className="w-8 h-8 text-orange-600 mb-3" />
            <h3>Oct 27th 2025</h3>
            <p>Executive update and production deployments</p>
            <Link to="/oct27-report" className="demo-link">View Report →</Link>
          </div>
          
          <div className="demo-card">
            <Calendar className="w-8 h-8 text-cyan-600 mb-3" />
            <h3>Nov 24th 2025</h3>
            <p>Prequal, Kinetic, and Baldwin updates</p>
            <Link to="/nov24-report" className="demo-link">View Report →</Link>
          </div>
          
          <div className="demo-card">
            <Shield className="w-8 h-8 text-purple-600 mb-3" />
            <h3>Insurance Comparison</h3>
            <p>Interactive policy comparison and analysis tool</p>
            <Link to="/insurance-comparison" className="demo-link">View Tool →</Link>
          </div>
          
          <div className="demo-card">
            <Network className="w-8 h-8 text-indigo-600 mb-3" />
            <h3>RiskGuru Mind Map</h3>
            <p>Interactive feature mind map visualization</p>
            <Link to="/riskguru-mindmap" className="demo-link">View Mind Map →</Link>
          </div>
          
          <div className="demo-card">
            <Users className="w-8 h-8 text-pink-600 mb-3" />
            <h3>Customer Engagement</h3>
            <p>Customer engagement summary and analytics</p>
            <Link to="/customer-engagement" className="demo-link">View Report →</Link>
          </div>
          
          <div className="demo-card">
            <Users className="w-8 h-8 text-blue-600 mb-3" />
            <h3>Engagement - Internal</h3>
            <p>Detailed internal report with all metrics</p>
            <Link to="/customer-engagement-internal" className="demo-link">View Report →</Link>
          </div>
          
          <div className="demo-card">
            <Users className="w-8 h-8 text-green-600 mb-3" />
            <h3>Engagement - Summary</h3>
            <p>Executive highlights and key metrics</p>
            <Link to="/customer-engagement-summary" className="demo-link">View Report →</Link>
          </div>
          
          <div className="demo-card">
            <Calendar className="w-8 h-8 text-teal-600 mb-3" />
            <h3>December Report</h3>
            <p>Last two weeks - Asuretify, Kinetic, and Wrap updates</p>
            <Link to="/nov-status-report" className="demo-link">View Report →</Link>
          </div>
          
          <div className="demo-card">
            <Calendar className="w-8 h-8 text-indigo-600 mb-3" />
            <h3>January 19th 2025</h3>
            <p>Team & Product Updates - Status Report</p>
            <Link to="/jan19-status-report" className="demo-link">View Report →</Link>
          </div>
          
          <div className="demo-card">
            <Calendar className="w-8 h-8 text-blue-600 mb-3" />
            <h3>February 2nd 2026</h3>
            <p>Executive Summary - Last Two Weeks</p>
            <Link to="/feb2-executive-summary" className="demo-link">View Report →</Link>
          </div>
          
          <div className="demo-card">
            <Calendar className="w-8 h-8 text-teal-600 mb-3" />
            <h3>February 9th 2025</h3>
            <p>Wrap & Asuretify Updates - Last Two Weeks</p>
            <Link to="/feb9-report" className="demo-link">View Report →</Link>
          </div>
          
          <div className="demo-card">
            <Calendar className="w-8 h-8 text-emerald-600 mb-3" />
            <h3>February 24th 2026</h3>
            <p>Weekly Report - Word/PDF, DOCX, Kinetic, Agent Portal</p>
            <Link to="/feb24-report" className="demo-link">View Report →</Link>
          </div>
          
          <div className="demo-card">
            <FileText className="w-8 h-8 text-yellow-600 mb-3" />
            <h3>Cost Report</h3>
            <p>Organization cost breakdown - Dec 9, 2025 to Jan 8, 2026</p>
            <Link to="/cost-report" className="demo-link">View Report →</Link>
          </div>

          <div className="demo-card">
            <Zap className="w-8 h-8 text-violet-600 mb-3" />
            <h3>LLM Classification</h3>
            <p>LLM-based classification & extraction process demo</p>
            <Link to="/llm-classification" className="demo-link">View Demo →</Link>
          </div>

          <div className="demo-card">
            <ListTodo className="w-8 h-8 text-indigo-600 mb-3" />
            <h3>Task Router</h3>
            <p>Lockton task routing prototype</p>
            <Link to="/task-router" className="demo-link">View Prototype →</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const App = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <Router>
      <AuthGuard>
        <div className="app">
          <Navigation isCollapsed={isCollapsed} toggleCollapse={toggleCollapse} />
          <main className={`main-content ${isCollapsed ? 'collapsed' : ''}`}>
            <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/sep4-status-report" element={<Sep4StatusReport />} />
            <Route path="/sep4/rag" element={<ConstructionRAGDemo />} />
            <Route path="/sep4/ai-agents" element={<ConstructionAgentsDemo />} />
            <Route path="/sep4/moe" element={<ConstructionMoEDemo />} />
            <Route path="/sep4/vector-db" element={<PolicyContentIntentDemo />} />
            <Route path="/status-report" element={<Sprint10Report />} />
            <Route path="/sep29-report" element={<Sep29Report />} />
            <Route path="/oct27-report" element={<Oct27Report />} />
            <Route path="/nov24-report" element={<Nov24Report />} />
            <Route path="/nov-status-report" element={<NovStatusReport />} />
            <Route path="/jan19-status-report" element={<Jan19StatusReport />} />
            <Route path="/feb2-executive-summary" element={<Feb2ExecutiveSummary />} />
            <Route path="/feb9-report" element={<Feb9Report />} />
            <Route path="/feb24-report" element={<Feb24Report />} />
            <Route path="/insurance-comparison" element={<InsuranceComparisonWireframe />} />
            <Route path="/single-document-check" element={<SingleDocumentCheck />} />
            <Route path="/riskguru-mindmap" element={<RiskGuruMindmap />} />
            <Route path="/task-router" element={<TaskRouter />} />
            <Route path="/customer-engagement" element={<CustomerEngagementReport />} />
            <Route path="/customer-engagement-internal" element={<CustomerEngagementReportInternal />} />
            <Route path="/customer-engagement-summary" element={<CustomerEngagementReportSummary />} />
            <Route path="/cost-report" element={<DecJanCostReport />} />
            <Route path="/llm-classification" element={<LLMClassificationDemo />} />
            <Route path="/ai-cost-calculator" element={<AICostCalculator />} />
          </Routes>
        </main>
      </div>
      </AuthGuard>
    </Router>
  );
};

export default App;
